import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { useDefectLevelValues } from '@/models/defectLevelValue';
import { useCurrentOrganization } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';

import { getDefectLevelValuesTree, IDefectLevelValue, IDefectLevelValues } from './DefectsTable';

export const DefectLevelValues = ({
  defectLevelValueForDefectType,
  setDefectLevelValueForDefectType,
}: {
  defectLevelValueForDefectType: IDefectLevelValue | null;
  setDefectLevelValueForDefectType: (value: IDefectLevelValue | null) => void;
}) => {
  const [currentOrganization] = useCurrentOrganization();

  const { data: defectLevelValueOptions = [] } = useDefectLevelValues({
    organizationId: currentOrganization?.id,
  });

  const initialDefectLevelValues =
    defectLevelValueForDefectType && defectLevelValueOptions.length
      ? getDefectLevelValuesTree(defectLevelValueForDefectType, defectLevelValueOptions)
      : [];

  const [defectLevelValues, setSelectedDefectLevelValues] =
    useState<IDefectLevelValue[]>(initialDefectLevelValues);

  if (!defectLevelValueOptions.length) {
    return;
  }

  const lastDefectLevelValue = defectLevelValues.length
    ? defectLevelValues[defectLevelValues.length - 1]
    : null;

  const canLatestValueHaveChild = lastDefectLevelValue
    ? defectLevelValueOptions.some((option) => option.parentId === lastDefectLevelValue.id)
    : null;

  const commonProps = {
    defectLevelValueOptions,
    defectLevelValues,
    setSelectedDefectLevelValues: (values: IDefectLevelValue[]) => {
      const lastDefectLevelValueOfTree = values[values.length - 1];

      const canLatestValueHaveChild = lastDefectLevelValueOfTree
        ? defectLevelValueOptions.some(
            (option) => option.parentId === lastDefectLevelValueOfTree.id
          )
        : null;

      setDefectLevelValueForDefectType(
        canLatestValueHaveChild ? null : (lastDefectLevelValueOfTree ?? null)
      );
      setSelectedDefectLevelValues(values);
    },
  };

  return (
    <>
      {defectLevelValues.map((defectLevelValue) => {
        return (
          <DefectLevelValue
            {...commonProps}
            key={defectLevelValue.id}
            defectLevelValue={defectLevelValue}
          />
        );
      })}

      {!defectLevelValues.length || canLatestValueHaveChild ? (
        <DefectLevelValue {...commonProps} key="new-defect-level-value" defectLevelValue={null} />
      ) : null}
    </>
  );
};

const DefectLevelValue = ({
  defectLevelValue,
  defectLevelValues,
  setSelectedDefectLevelValues,
  defectLevelValueOptions,
}: {
  defectLevelValue: IDefectLevelValue | null;
  defectLevelValues: IDefectLevelValue[];
  setSelectedDefectLevelValues: (defectLevelValues: IDefectLevelValue[]) => void;
  defectLevelValueOptions: IDefectLevelValues;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const selectedDefectLevelValue =
    defectLevelValueOptions.find((option) => option.id === defectLevelValue?.id) ?? null;

  const label = t({
    id: 'settings.defect-types.drawer.defect-level-value.label',
    message: 'Defect level',
  });

  const hasChild = defectLevelValues.some((defectLevelValue) => {
    return !!selectedDefectLevelValue && defectLevelValue?.parentId === selectedDefectLevelValue.id;
  });

  let filteredOptions: IDefectLevelValues = [];

  // The input is the first of tree
  if (
    !defectLevelValues.length ||
    (selectedDefectLevelValue && !selectedDefectLevelValue?.parentId)
  ) {
    filteredOptions = defectLevelValueOptions.filter((option) => !option.parentId);
  }

  // The input is empty and has parents
  else if (defectLevelValues.length && !selectedDefectLevelValue) {
    const parent = defectLevelValues[defectLevelValues.length - 1];

    filteredOptions = defectLevelValueOptions.filter((option) => option.parentId === parent.id);
  }

  // The input is NOT empty and has parents
  else {
    const parent = defectLevelValues[defectLevelValues.length - 2];

    filteredOptions = defectLevelValueOptions.filter((option) => option.parentId === parent.id);
  }

  return (
    <InputSelect
      label={label}
      placeholder={label}
      value={selectedDefectLevelValue ?? null}
      options={filteredOptions}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => _db(option.name)}
      isDisabled={hasChild}
      onChange={(newDefectLevelValue) => {
        if (hasChild) {
          // We don't want to allow update if a child has been set locally
          // The user needs to remove the child first
          return;
        }

        if (newDefectLevelValue) {
          const newDefectLevelValues = getDefectLevelValuesTree(
            newDefectLevelValue,
            defectLevelValueOptions
          );

          setSelectedDefectLevelValues(newDefectLevelValues);
        } else {
          const newDefectLevelValues = defectLevelValues.slice(0, defectLevelValues.length - 1);

          setSelectedDefectLevelValues(newDefectLevelValues);

          return;
        }
      }}
      isClearable
    />
  );
};
